import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Music, Instagram, Youtube, Twitch, Play, Mail, ExternalLink, QrCode } from 'lucide-react';
import Nav from '../components/Nav';

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const socialLinks = [
    { icon: <Instagram />, url: 'https://www.instagram.com/dionlovelle', color: '#E4405F', name: 'Instagram' },
    { icon: <Youtube />, url: 'https://www.youtube.com/dionlovelle', color: '#FF0000', name: 'YouTube' },
    { icon: <Twitch />, url: 'https://www.twitch.tv/dionlovellej', color: '#9146FF', name: 'Twitch' },
    { icon: <Music />, url: 'https://www.tiktok.com/@dionlovelle', color: '#000', name: 'TikTok' },
    { icon: <Music />, url: 'https://open.spotify.com/artist/2WUPin61rUCoiVp4rQxiVq', color: '#1DB954', name: 'Spotify' },
  ];

  const spotifyReleases = [
    {
      id: '0fl1xqfuN53rQMdwOP3vbE',
      title: 'All To Myself',
      year: '2025',
      type: 'Single',
      description: 'A soulful acoustic confession — stripped down and intimate.',
      url: 'https://open.spotify.com/album/0fl1xqfuN53rQMdwOP3vbE'
    },
    {
      id: '0vVJDGSzVAoy8DhSlnyYYl',
      title: 'Love in LA',
      year: '2025',
      type: 'Single',
      description: 'A romantic West Coast groove capturing late nights and city lights.',
      url: 'https://open.spotify.com/album/0vVJDGSzVAoy8DhSlnyYYl'
    },
  ];

  const videos = [
    {
      id: 'DP9jWsgEu2D',
      type: 'instagram',
      title: 'Latest Performance',
      url: 'https://www.instagram.com/reel/DP9jWsgEu2D/?igsh=NTc4MTIwNjQ2YQ=='
    },
    {
      id: 'jCV6tddVlmU',
      type: 'youtube',
      title: 'Jigsaw Falling Into Place - Radiohead Cover',
      url: 'https://youtu.be/jCV6tddVlmU?si=dNxw-cNSlwquYNHF'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <Nav />

      {/* Value Prop Banner */}
      <section className="pt-20 sm:pt-24 pb-2 px-4 sm:px-6" data-testid="artist-intro-banner">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card px-6 py-6 sm:px-10 sm:py-8 text-center border border-purple-500/30">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-3 leading-tight">
              Live Music for Weddings &amp; Private Events
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl mx-auto">
              From intimate acoustic performances to full-band experiences — professional, seamless, and tailored to your event.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Performance Reel */}
      <section className="pt-4 pb-8 sm:pb-10 px-4 sm:px-6" data-testid="featured-reel-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-4 sm:mb-6">
            <p className="text-xs uppercase tracking-widest text-purple-300 mb-1">Featured Performance</p>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text">
              &ldquo;Ooh Baby Baby&rdquo; — Smokey Robinson
            </h3>
          </div>
          <div className="glass-card p-3 sm:p-4 border border-purple-500/30">
            <div className="instagram-embed rounded-xl overflow-hidden mx-auto" style={{ maxWidth: '420px' }}>
              <iframe
                data-testid="featured-reel-iframe"
                src="https://www.instagram.com/reel/DXLQUz6gtfD/embed"
                width="100%"
                height="640"
                frameBorder="0"
                scrolling="no"
                allowFullScreen
                allow="autoplay; encrypted-media; picture-in-picture; web-share"
                title="Ooh Baby Baby - Smokey Robinson cover"
                style={{ display: 'block', background: 'transparent' }}
              />
            </div>
            <div className="text-center mt-3">
              <a
                href="https://www.instagram.com/reel/DXLQUz6gtfD/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-pink-400 hover:text-pink-300 transition-colors text-sm font-semibold"
                data-testid="featured-reel-link"
              >
                <Instagram size={16} />
                Watch on Instagram
                <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-8">
        <div 
          className="parallax-bg"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        />
        <div className="relative z-10 text-center px-6 animate-fade-in">
          <div className="mb-8">
            <img 
              src="/dionlovelle-hero.jpg" 
              alt="Dionlovelle"
              className="w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80 rounded-full mx-auto border-4 border-white/20 shadow-2xl"
              style={{ 
                boxShadow: '0 0 100px rgba(139, 92, 246, 0.5)',
                objectFit: 'cover',
                objectPosition: 'center 15%'
              }}
            />
          </div>
          <h1 className="hero-title mb-6 px-4" data-testid="hero-title">DIONLOVELLE</h1>
          <p className="text-xl sm:text-2xl md:text-3xl mb-4 text-gray-300 font-light px-4">Live Music for Weddings &amp; Private Events</p>
          <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-8 px-4">
            From intimate acoustic performances to full-band experiences — professional, seamless, and tailored to your event.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a 
              href="https://open.spotify.com/artist/2WUPin61rUCoiVp4rQxiVq" 
              target="_blank" 
              rel="noopener noreferrer"
              className="glow-button"
              data-testid="listen-now-button"
            >
              <Play className="inline mr-2" size={20} />
              Listen Now
            </a>
            <Link to="/booking" className="glow-button" data-testid="hero-booking-button">
              Book Now
            </Link>
          </div>
        </div>
      </section>

      {/* New Releases - Streaming Section */}
      <section id="music" className="py-12 sm:py-20 px-4 sm:px-6" data-testid="new-releases-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs sm:text-sm font-bold px-4 sm:px-6 py-2 rounded-full mb-4">
              NEW RELEASES
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 gradient-text">Latest Singles</h2>
            <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
              Stream my latest songs now on Spotify and all major platforms
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {spotifyReleases.map((release, index) => (
              <div key={release.id} className="glass-card p-5 sm:p-6 group" data-testid={`spotify-release-${index}`}>
                <div className="mb-4">
                  <h3 className="text-2xl sm:text-3xl font-bold mb-2 group-hover:text-purple-400 transition-colors">{release.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                    <span className="px-2 py-1 bg-purple-500/20 rounded-full text-xs">{release.year}</span>
                    <span>•</span>
                    <span>{release.type}</span>
                  </div>
                  <p className="text-sm text-gray-400 italic mb-3">{release.description}</p>
                </div>
                <div className="spotify-embed rounded-xl overflow-hidden mb-4">
                  <iframe
                    data-testid={`spotify-iframe-${index}`}
                    src={`https://open.spotify.com/embed/album/${release.id}?utm_source=generator&theme=0`}
                    width="100%"
                    height="352"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    title={release.title}
                    style={{ borderRadius: '12px' }}
                  />
                </div>
                <a
                  href={release.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-green-500 hover:text-green-400 transition-colors font-semibold"
                  data-testid={`spotify-link-${index}`}
                >
                  <Music size={18} />
                  Open in Spotify
                  <ExternalLink size={14} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Music Videos Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black via-purple-900/10 to-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4 gradient-text" data-testid="music-section-title">Music Videos</h2>
          <p className="text-center text-gray-400 mb-12 text-lg">Watch my latest visuals</p>

          <div className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {videos.map((video, index) => (
                <div key={index} className="glass-card p-6 group" data-testid={`video-${index}`}>
                  <div className={`${video.type === 'youtube' ? 'youtube-embed' : 'instagram-embed'} aspect-video rounded-xl overflow-hidden mb-4`}>
                    {video.type === 'youtube' ? (
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${video.id}`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="w-full h-full"
                        style={{ minHeight: '300px' }}
                      />
                    ) : (
                      <iframe
                        src={`https://www.instagram.com/reel/${video.id}/embed`}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        scrolling="no"
                        allowFullScreen
                        className="w-full h-full"
                        style={{ minHeight: '300px' }}
                      />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold">{video.title}</h4>
                    <a 
                      href={video.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${video.type === 'youtube' ? 'text-red-500 hover:text-red-400' : 'text-pink-500 hover:text-pink-400'} transition-colors`}
                      data-testid={`video-link-${index}`}
                    >
                      <ExternalLink size={20} />
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="glass-card p-8 max-w-4xl mx-auto text-center">
              <h4 className="text-2xl font-bold mb-4">Explore More Content</h4>
              <p className="text-gray-400 mb-6">Don&apos;t miss out on behind-the-scenes, live performances, and exclusive content</p>
              <div className="flex gap-4 justify-center flex-wrap">
                <a 
                  href="https://www.youtube.com/dionlovelle" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="glow-button inline-flex items-center gap-2"
                  data-testid="youtube-channel-button"
                >
                  <Youtube size={24} />
                  YouTube
                </a>
                <a 
                  href="https://www.instagram.com/dionlovelle" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="glow-button inline-flex items-center gap-2"
                  data-testid="instagram-button"
                >
                  <Instagram size={24} />
                  Instagram
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Albums Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black via-purple-900/10 to-black">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8 mb-12">
            <div className="md:w-1/3">
              <img 
                src="/dionlovelle-photo.jpg" 
                alt="Dionlovelle in studio"
                className="rounded-2xl shadow-2xl w-full object-cover"
                style={{ boxShadow: '0 0 60px rgba(212, 175, 55, 0.3)' }}
              />
            </div>
            <div className="md:w-2/3 text-center md:text-left">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">Featured Albums</h2>
              <p className="text-lg text-gray-300 mb-4">
                Explore my complete discography - from the celestial sounds of Analemma to the raw energy of Eat The Sun.
              </p>
              <p className="text-gray-400">
                Each album tells a unique story, blending soulful melodies with powerful vocals and unforgettable performances.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Analemma Album */}
            <div className="glass-card p-8 group cursor-pointer" data-testid="album-analemma">
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="w-40 h-40 bg-gradient-to-br from-purple-500 to-gold-500 rounded-lg flex items-center justify-center text-6xl font-bold shadow-2xl group-hover:scale-105 transition-transform">
                  🎵
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-3xl font-bold mb-2 group-hover:text-purple-400 transition-colors">Analemma</h3>
                  <p className="text-gray-400 mb-3">Album • 2022</p>
                  <p className="text-sm text-gray-500 mb-4">A celestial journey through sound and emotion</p>
                  <a 
                    href="https://open.spotify.com/artist/2WUPin61rUCoiVp4rQxiVq"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-green-500 hover:text-green-400 transition-colors"
                  >
                    <Music size={18} />
                    Listen on Spotify
                  </a>
                </div>
              </div>
            </div>

            {/* Eat The Sun Album */}
            <div className="glass-card p-8 group cursor-pointer" data-testid="album-eat-the-sun">
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="w-40 h-40 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center text-6xl font-bold shadow-2xl group-hover:scale-105 transition-transform">
                  ☀️
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-3xl font-bold mb-2 group-hover:text-purple-400 transition-colors">Eat The Sun</h3>
                  <p className="text-gray-400 mb-3">Album • 2016</p>
                  <p className="text-sm text-gray-500 mb-4">Raw energy and passion in every note</p>
                  <a 
                    href="https://open.spotify.com/artist/2WUPin61rUCoiVp4rQxiVq"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-green-500 hover:text-green-400 transition-colors"
                  >
                    <Music size={18} />
                    Listen on Spotify
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-5xl font-bold mb-4 gradient-text" data-testid="connect-section-title">Connect With Me</h2>
          <p className="text-gray-400 mb-12 text-lg">Follow my journey across all platforms</p>
          <div className="flex justify-center gap-6 flex-wrap">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
                style={{ borderColor: `${social.color}40` }}
                title={social.name}
                data-testid={`social-link-${index}`}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact/CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black via-purple-900/10 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <div className="glass-card p-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 gradient-text" data-testid="cta-title">Ready to Work Together?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Book me for session vocals, live performances, writing, or special appearances.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/booking" className="glow-button text-lg" data-testid="cta-booking-button">
                View Booking Options
              </Link>
              <a 
                href="mailto:dionlovellebooking@gmail.com"
                className="glow-button text-lg"
                data-testid="email-button"
              >
                <Mail className="inline mr-2" size={20} />
                dionlovellebooking@gmail.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Section */}
      <section className="py-10 px-6 border-t border-white/10">
        <div className="max-w-3xl mx-auto text-center">
          <Link 
            to="/donate"
            className="inline-flex items-center gap-2 px-8 py-3 glow-button text-lg"
            data-testid="donate-button"
          >
            <span>💜</span>
            <span className="font-semibold">Donate</span>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400">© 2025 Dionlovelle. All rights reserved.</p>
          <p className="text-sm text-gray-500 mt-2">dionlovellebooking@gmail.com</p>
          <div className="mt-6">
            <Link
              to="/live"
              className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-purple-300 transition-colors border-b border-dotted border-gray-700 hover:border-purple-300 pb-0.5"
              data-testid="footer-live-qr-link"
              aria-label="Open Live QR page"
            >
              <QrCode size={12} />
              Live QR page
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;